import React, { useState, useEffect } from 'react';
import { RefreshCw, ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, Building2, Loader2, Search, X } from 'lucide-react';
import api from '../services/api';

export default function SyncCompaniesModal({ isOpen, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [swibi, setSwibi] = useState([]);
  const [npr, setNpr] = useState([]);
  const [selectedSwibi, setSelectedSwibi] = useState(new Set());
  const [selectedNpr, setSelectedNpr] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [searchSwibi, setSearchSwibi] = useState('');
  const [searchNpr, setSearchNpr] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setSelectedSwibi(new Set());
    setSelectedNpr(new Set());
    setResult(null);
    api.get('/integration/sync-companies/preview')
      .then(r => { setSwibi(r.data.swibi); setNpr(r.data.npr); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleSwibi = (id) => setSelectedSwibi(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleNpr = (id) => setSelectedNpr(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const selectAllSwibi = () => {
    const filtered = filteredSwibi.filter(c => !c.is_duplicate && !c.is_synced);
    setSelectedSwibi(new Set(filtered.map(c => c.id)));
  };
  const selectAllNpr = () => {
    const filtered = filteredNpr.filter(c => !c.is_duplicate && !c.is_synced);
    setSelectedNpr(new Set(filtered.map(c => c.id)));
  };

  const sendToNpr = async () => {
    setSending(true);
    try {
      const r = await api.post('/integration/sync-companies/to-npr', { company_ids: [...selectedSwibi] });
      setResult({ type: 'success', message: r.data.message });
      setSelectedSwibi(new Set());
      // Odśwież
      const preview = await api.get('/integration/sync-companies/preview');
      setSwibi(preview.data.swibi); setNpr(preview.data.npr);
    } catch (err) { setResult({ type: 'error', message: err.response?.data?.error || 'Błąd' }); }
    finally { setSending(false); }
  };

  const importFromNpr = async () => {
    setSending(true);
    try {
      const companies = npr.filter(c => selectedNpr.has(c.id));
      const r = await api.post('/integration/sync-companies/from-npr', { companies });
      setResult({ type: 'success', message: r.data.message });
      setSelectedNpr(new Set());
      const preview = await api.get('/integration/sync-companies/preview');
      setSwibi(preview.data.swibi); setNpr(preview.data.npr);
      if (onDone) onDone();
    } catch (err) { setResult({ type: 'error', message: err.response?.data?.error || 'Błąd' }); }
    finally { setSending(false); }
  };

  const filter = (list, search) => {
    if (!search) return list;
    const s = search.toLowerCase();
    return list.filter(c => c.name.toLowerCase().includes(s) || (c.nip && c.nip.includes(s)) || (c.address_city && c.address_city.toLowerCase().includes(s)));
  };

  const filteredSwibi = filter(swibi, searchSwibi);
  const filteredNpr = filter(npr, searchNpr);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-5xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <RefreshCw size={20} className="text-emerald-600" /> Synchronizacja firm SWIBI ↔ NPR
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
        </div>

        {/* Result banner */}
        {result && (
          <div className={`mx-6 mt-3 p-3 rounded-lg text-sm flex items-center gap-2 ${result.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {result.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
            {result.message}
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-12">
            <Loader2 size={24} className="animate-spin text-gray-400" />
            <span className="ml-2 text-gray-400">Ładowanie firm z obu systemów...</span>
          </div>
        ) : (
          <>
            {/* Panels */}
            <div className="flex-1 flex overflow-hidden px-6 py-4 gap-4 min-h-0">
              {/* SWIBI panel */}
              <div className="flex-1 flex flex-col border border-blue-200 rounded-lg overflow-hidden">
                <div className="bg-blue-50 px-3 py-2 border-b border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-blue-800">SWIBI ({swibi.length})</span>
                    <button onClick={selectAllSwibi} className="text-xs text-blue-600 hover:underline">Zaznacz nowe</button>
                  </div>
                  <div className="relative">
                    <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" value={searchSwibi} onChange={e => setSearchSwibi(e.target.value)}
                      placeholder="Szukaj..." className="w-full pl-7 pr-2 py-1 border border-blue-200 rounded text-xs" />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {filteredSwibi.map(c => (
                    <label key={c.id} className={`flex items-start gap-2 px-3 py-2 border-b border-gray-100 text-sm cursor-pointer hover:bg-gray-50 ${c.is_duplicate ? 'bg-amber-50' : ''} ${c.is_synced ? 'bg-green-50' : ''}`}>
                      <input type="checkbox" checked={selectedSwibi.has(c.id)} onChange={() => toggleSwibi(c.id)}
                        disabled={c.is_duplicate || c.is_synced}
                        className="mt-1 rounded border-gray-300" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 truncate">{c.name}</div>
                        <div className="text-xs text-gray-500">
                          {c.nip && <span>NIP: {c.nip}</span>}
                          {c.address_city && <span> • {c.address_city}</span>}
                        </div>
                        {c.is_synced && <span className="text-xs text-green-600">✓ Zsynchronizowana</span>}
                        {c.is_duplicate && !c.is_synced && <span className="text-xs text-amber-600">⚠ Istnieje w NPR</span>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Center arrows */}
              <div className="flex flex-col items-center justify-center gap-3 flex-shrink-0">
                <button onClick={sendToNpr} disabled={sending || selectedSwibi.size === 0}
                  className="flex items-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-30 text-white rounded-lg text-xs font-medium">
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  Do NPR ({selectedSwibi.size})
                </button>
                <button onClick={importFromNpr} disabled={sending || selectedNpr.size === 0}
                  className="flex items-center gap-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-30 text-white rounded-lg text-xs font-medium">
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <ArrowLeft size={14} />}
                  Z NPR ({selectedNpr.size})
                </button>
              </div>

              {/* NPR panel */}
              <div className="flex-1 flex flex-col border border-emerald-200 rounded-lg overflow-hidden">
                <div className="bg-emerald-50 px-3 py-2 border-b border-emerald-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-emerald-800">NPR ({npr.length})</span>
                    <button onClick={selectAllNpr} className="text-xs text-emerald-600 hover:underline">Zaznacz nowe</button>
                  </div>
                  <div className="relative">
                    <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" value={searchNpr} onChange={e => setSearchNpr(e.target.value)}
                      placeholder="Szukaj..." className="w-full pl-7 pr-2 py-1 border border-emerald-200 rounded text-xs" />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {filteredNpr.map(c => (
                    <label key={c.id} className={`flex items-start gap-2 px-3 py-2 border-b border-gray-100 text-sm cursor-pointer hover:bg-gray-50 ${c.is_duplicate ? 'bg-amber-50' : ''} ${c.is_synced ? 'bg-green-50' : ''}`}>
                      <input type="checkbox" checked={selectedNpr.has(c.id)} onChange={() => toggleNpr(c.id)}
                        disabled={c.is_duplicate || c.is_synced}
                        className="mt-1 rounded border-gray-300" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 truncate">{c.name}</div>
                        <div className="text-xs text-gray-500">
                          {c.nip && <span>NIP: {c.nip}</span>}
                          {c.address_city && <span> • {c.address_city}</span>}
                        </div>
                        {c.is_synced && <span className="text-xs text-green-600">✓ Zsynchronizowana</span>}
                        {c.is_duplicate && !c.is_synced && <span className="text-xs text-amber-600">⚠ Istnieje w SWIBI</span>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t bg-gray-50 flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-100 border border-green-300 rounded inline-block"></span> Zsynchronizowane</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-100 border border-amber-300 rounded inline-block"></span> Duplikat (NIP)</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-white border border-gray-300 rounded inline-block"></span> Nowe (do synchronizacji)</span>
              </div>
              <button onClick={onClose} className="px-4 py-1.5 text-gray-600 hover:bg-gray-200 rounded-lg">Zamknij</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
